import { createRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { Route as AdminLayoutRoute } from "./_layout";
import { Avatar } from "@/components/base/avatar/avatar";
import { Badge, BadgeWithDot } from "@/components/base/badges/badges";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { Card, CardBody, CardHeader } from "@/components/application/card/card";
import { DataTable, type Column } from "@/components/application/data-table/data-table";
import {
  useUserDetail,
  useAuditLogRecent,
  type AuditLogEntry,
} from "../../hooks/useUserDetailQueries";
import { usePushAssistDetail } from "../../hooks/usePushAssistQueries";

export const Route = createRoute({
  getParentRoute: () => AdminLayoutRoute,
  path: "/admin/users/$userId",
  component: UserDetailPage,
});

function UserDetailPage() {
  const { userId } = Route.useParams();
  const userQuery = useUserDetail(userId);
  const pushAssistQuery = usePushAssistDetail(
    userQuery.data?.push_assist_mode ? userId : null,
  );
  const auditQuery = useAuditLogRecent();

  const tenantId = userQuery.data?.tenant_id;
  const recentEvents = useMemo(() => {
    if (!auditQuery.data || !tenantId) return [];
    return auditQuery.data.filter((e) => e.tenantId === tenantId).slice(0, 20);
  }, [auditQuery.data, tenantId]);

  const user = userQuery.data;
  const errMessage = userQuery.error instanceof Error ? userQuery.error.message : null;
  const isNotFound = errMessage != null && /not found|404|nenalezen/i.test(errMessage);

  return (
    <section aria-labelledby="user-detail-title" className="max-w-7xl mx-auto space-y-6">
      {!user && (
        <h2 id="user-detail-title" className="text-display-xs font-bold text-primary m-0">
          Uživatel
        </h2>
      )}

      {userQuery.isLoading && (
        <p className="text-tertiary" aria-busy="true">
          Načítám detail uživatele…
        </p>
      )}

      {userQuery.isError && (
        <InlineAlert variant="error">
          {isNotFound ? "Uživatel nenalezen." : `Načtení selhalo: ${errMessage ?? "Neznámá chyba"}`}
        </InlineAlert>
      )}

      {user && (
        <>
          <Card>
            <CardBody>
              <div className="flex items-start gap-5 flex-wrap">
                <Avatar
                  initials={getInitials(user.display_name || user.email || "?")}
                  alt={user.display_name || user.email || "Uživatel"}
                  size="xl"
                />
                <div className="flex-1 min-w-0">
                  <h2
                    id="user-detail-title"
                    className="text-display-xs font-bold text-primary m-0"
                  >
                    {user.display_name || user.email || "—"}
                  </h2>
                  {user.email && (
                    <p className="text-md text-tertiary m-0 mt-0.5">{user.email}</p>
                  )}
                  <div className="flex gap-2 flex-wrap mt-3">
                    <BadgeWithDot color={roleBadgeColor(user.role)}>
                      {roleLabel(user.role)}
                    </BadgeWithDot>
                    {user.deactivated_at ? (
                      <Badge color="gray">Deaktivován</Badge>
                    ) : user.locked_until && new Date(user.locked_until) > new Date() ? (
                      <BadgeWithDot color="error">
                        Uzamčen
                      </BadgeWithDot>
                    ) : (
                      <BadgeWithDot color="success">
                        Aktivní
                      </BadgeWithDot>
                    )}
                    <BadgeWithDot color={consentBadgeColor(user.consent_status)}>
                      {consentStatusLabel(user.consent_status)}
                    </BadgeWithDot>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card>
              <CardHeader title="Identita" />
              <CardBody>
                <DefinitionList
                  items={[
                    {
                      label: "Středisko (tenant)",
                      value: (
                        <span className="text-xs" title={user.tenant_id}>
                          {user.tenant_id.slice(0, 8)}…
                        </span>
                      ),
                    },
                    { label: "Neúspěšné přihlášení", value: `${user.failed_login_count}×` },
                    {
                      label: "První přihlášení",
                      value: user.first_login_at
                        ? new Date(user.first_login_at).toLocaleString("cs-CZ")
                        : "—",
                    },
                    { label: "Vytvořeno", value: new Date(user.created_at).toLocaleString("cs-CZ") },
                  ]}
                />
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Souhlas" />
              <CardBody>
                <DefinitionList
                  items={[
                    {
                      label: "Stav",
                      value: (
                        <Badge color={consentBadgeColor(user.consent_status)}>
                          {consentStatusLabel(user.consent_status)}
                        </Badge>
                      ),
                    },
                    {
                      label: "Verze textu",
                      value:
                        user.consent_text_version ?? (
                          <span className="text-tertiary">žádná</span>
                        ),
                    },
                  ]}
                />
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Push-assist" />
              <CardBody>
                {!user.push_assist_mode ? (
                  <p className="text-tertiary m-0">Uživatel není v režimu push-assist.</p>
                ) : pushAssistQuery.isLoading ? (
                  <p className="text-tertiary m-0">Načítám push-assist data…</p>
                ) : pushAssistQuery.isError ? (
                  <InlineAlert variant="error">Načtení push-assist detailu selhalo.</InlineAlert>
                ) : pushAssistQuery.data ? (
                  <DefinitionList
                    items={[
                      {
                        label: "Papírový souhlas",
                        value: pushAssistQuery.data.has_valid_paper_consent ? (
                          <BadgeWithDot color="success">
                            Platný
                          </BadgeWithDot>
                        ) : (
                          <BadgeWithDot color="warning">
                            Chybí
                          </BadgeWithDot>
                        ),
                      },
                      { label: "Naskenovaných souhlasů", value: pushAssistQuery.data.paper_consents.length },
                      { label: "Doručené QR/odkazy", value: pushAssistQuery.data.deliveries.length },
                    ]}
                  />
                ) : (
                  <p className="text-tertiary m-0">Žádná data.</p>
                )}
              </CardBody>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <div>
                <h3 className="text-lg font-semibold text-primary m-0">Nedávné akce v auditu</h3>
                <p className="text-sm text-tertiary m-0 mt-0.5">
                  Posledních 20 akcí v rámci tohoto Střediska. Záznamy jen pro připojení.
                </p>
              </div>
            </CardHeader>
            <CardBody>
              {auditQuery.isLoading && <p className="text-tertiary m-0">Načítám audit log…</p>}
              {auditQuery.isError && <InlineAlert variant="error">Načtení audit logu selhalo.</InlineAlert>}
              {!auditQuery.isLoading && !auditQuery.isError && (
                <DataTable
                  caption="Nedávné akce v auditu pro tenanta tohoto uživatele"
                  columns={auditColumns}
                  rows={recentEvents}
                  rowKey={(e) => e.id}
                  emptyMessage="Žádné akce v auditu pro tento tenant."
                />
              )}
            </CardBody>
          </Card>
        </>
      )}
    </section>
  );
}

interface DefinitionItem {
  label: string;
  value: React.ReactNode;
}

function DefinitionList({ items }: { items: DefinitionItem[] }) {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 m-0">
      {items.map((item) => (
        <div key={item.label} className="contents">
          <dt className="text-sm text-tertiary">{item.label}</dt>
          <dd className="text-sm text-primary m-0">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function getInitials(nameOrEmail: string): string {
  const cleaned = nameOrEmail.trim();
  if (!cleaned || cleaned === "?") return "?";
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return cleaned.slice(0, 2).toUpperCase();
}

const auditColumns: Column<AuditLogEntry>[] = [
  {
    key: "action",
    header: "Akce",
    cell: (e) => actionLabel(e.action),
  },
  {
    key: "time",
    header: "Čas",
    cell: (e) => new Date(e.createdAt).toLocaleString("cs-CZ"),
  },
];

function roleBadgeColor(role: string): "blue" | "success" | "warning" | "gray" {
  switch (role) {
    case "globalAdmin":
      return "warning";
    case "tenantAdmin":
      return "blue";
    case "subAdmin":
      return "blue";
    case "endUser":
      return "gray";
    default:
      return "gray";
  }
}

function roleLabel(role: string): string {
  switch (role) {
    case "globalAdmin":
      return "Globální administrátor";
    case "tenantAdmin":
      return "Administrátor Střediska";
    case "subAdmin":
      return "Vedoucí mikroskupiny";
    case "endUser":
      return "Uživatel";
    default:
      return role;
  }
}

function consentBadgeColor(status: string): "success" | "warning" | "error" | "gray" {
  switch (status) {
    case "active":
      return "success";
    case "pending_re_prompt":
      return "warning";
    case "withdrawn":
      return "error";
    case "none":
    default:
      return "gray";
  }
}

function consentStatusLabel(status: string): string {
  switch (status) {
    case "active":
      return "Aktivní";
    case "pending_re_prompt":
      return "Vyžaduje nový souhlas";
    case "withdrawn":
      return "Odvolán";
    case "none":
      return "Neudělen";
    default:
      return status;
  }
}

function actionLabel(action: string): string {
  switch (action) {
    case "login_success":
      return "Přihlášení";
    case "login_failure":
      return "Neúspěšné přihlášení";
    case "login_lockout":
      return "Účet uzamčen";
    case "logout":
      return "Odhlášení";
    case "password_changed":
      return "Změna hesla";
    case "consent_granted":
      return "Souhlas udělen";
    case "consent_withdrawn":
      return "Souhlas odvolán";
    case "rate_limited":
      return "Omezení rychlosti";
    case "magic_link_consumed":
      return "Magic odkaz použit";
    case "payment_anomaly_resolved":
      return "Anomálie vyřešena";
    case "push_assist_qr_generated":
      return "Vygenerován QR (push-assist)";
    case "push_assist_consent_uploaded":
      return "Nahrán papírový souhlas";
    case "push_assist_delivery_logged":
      return "Doručení push-assist zapsáno";
    case "tax_document_uploaded":
      return "Nahrán daňový doklad";
    case "microgroup_invitation_created":
      return "Pozvánka mikroskupiny vytvořena";
    case "microgroup_invitation_approved":
      return "Pozvánka schválena";
    case "distribution_triggered":
      return "Spuštěna distribuce";
    case "distribution_completed":
      return "Distribuce dokončena";
    default:
      return action;
  }
}
